// ============================================================
// The hider deck. As of v8 (BOLT-007) the economy numbers are
// DERIVED from the core Pack (CORE_DECK): SEEKER_COST from each
// seeker card's `cost`, and the hider COMPOSITION from each hider
// card's `hider` spec + `defaultCount`. One source, no duplication
// (full design §7).
// ============================================================
import type { CardFamily, HiderCard, TimedCurse } from './protocol';
import { resolveCount, type DeckConfig } from './cards';
import { CORE_DECK } from './packs/core';
import { mulberry32, shuffle } from './rng';

/** Hider cards a seeker question deals on resolution, by seeker card id
 *  (derived from CORE_DECK; Photo cards draw only on a *successful* upload). */
export const SEEKER_COST: Record<string, number> = Object.fromEntries(
  CORE_DECK.filter((c) => c.deck === 'seeker').map((c) => [c.id, c.cost ?? 0]),
);

/** Time-based blocking curses only (Portal is special — no duration). */
/** v18 BOLT-081: no per-card `settingKey` any more — every timed card runs on
 *  `settings.blockDurationMs`. The indirection existed only to hold three
 *  numbers apart; one number needs no indirection. */
export const CURSE_META: Record<TimedCurse, { label: string; instruction: string }> = {
  // v15 BOLT-054: Hold-Up = the movement freeze (cards still allowed, honor
  // system); Silence (wire kind 'snail', frozen) = the question block.
  holdup: { label: 'Freeze', instruction: "Stop where you are and don't move! You can still play cards." }, // v16 BOLT-068
  snail: { label: 'Silence', instruction: "You can't play any cards" },
  // v16 BOLT-071: capability-gated (minProtocol 6 on the card) — a v5 room
  // never builds this type, so the meta is only ever read in v6 rooms.
  babel: { label: 'Babel', instruction: "Hope you're fluent!" },
  // v18 BOLT-080: capability-gated at protocol 7 — a v6 room never builds it.
  flyingBlind: { label: 'Flying Blind', instruction: 'Your map is dark. The play area and the zone are all you get.' },
};

/** Deck composition — `typeId` is the stable catalog key (Card Settings, §5);
 *  `count` is the default copies. Derived from CORE_DECK's hider cards. */
type CompositionEntry = Omit<HiderCard, 'id'> & { typeId: string; count: number };
const COMPOSITION: CompositionEntry[] = CORE_DECK.filter((c) => c.deck === 'hider' && c.hider).map((c) => {
  const h = c.hider!;
  const entry: CompositionEntry = { typeId: c.id, count: c.defaultCount, kind: h.kind, label: c.label, effect: c.description };
  if (h.bonusMin !== undefined) entry.bonusMin = h.bonusMin;
  if (h.curse !== undefined) entry.curse = h.curse;
  return entry;
});

/** A portal curse opens placement on the client rather than the timed path. */
export const isPortalCard = (c: HiderCard): boolean => c.kind === 'curse' && c.curse === 'portal';

/** Build + shuffle a fresh deck. `seed > 0` makes the order deterministic.
 *  `deckConfig` overrides per-type copies (Card Settings, §5). */
export function buildHiderDeck(seed = 0, deckConfig?: DeckConfig, capabilityFloor = Infinity): HiderCard[] {
  const cards: HiderCard[] = [];
  let n = 0;
  for (const { typeId, count, ...rest } of COMPOSITION) {
    // v16 BOLT-073: capability gate — a version-barred card TYPE never enters
    // a room below its floor (a v5 decoder must never see it on any frame).
    const bar = CORE_DECK.find((d) => d.id === typeId)?.minProtocol ?? 0;
    if (bar > capabilityFloor) continue;
    const copies = resolveCount(deckConfig, typeId, count);
    for (let i = 0; i < copies; i++) cards.push({ ...rest, id: `h${n++}` });
  }
  const rng = seed > 0 ? mulberry32(seed) : Math.random;
  return shuffle(cards, rng);
}

export const familyOf = (cardId: string): CardFamily | null =>
  cardId.startsWith('radar')
    ? 'radar'
    : cardId.startsWith('thermo')
      ? 'thermometer'
      : cardId.startsWith('photo')
        ? 'photo'
        : cardId === 'direction'
          ? 'direction'
          : null;
